import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IRestaurantDetail } from "../models/IRestaurantDetail";
import { ChangeRestaurantStatus, getRestaurantById } from "../services/restaurant";
import { getActiveRestaurantId } from "../utils/activeRestaurant";
import { IRestaurantAvailability } from "../working-hours-module/models/IAvailability";
import { getAvailability, reopenRestaurant } from "../working-hours-module/services/availabilityService";

/**
 * Restoranı aç/kapat anahtarı.
 *
 * "Açık" iki şeyin birleşimidir (backend RestaurantAvailability kuralı):
 *   restoran açık bayrağı (bu anahtar)  VE  çalışma saati / kapama kaydı şu an açık diyor.
 * Anahtar yalnız bayrağı yazar; sunucu çalışma saatine bakıp reddetmez. Bu yüzden saat
 * tanımlı değilken ya da saat dışındayken anahtar "Açık" görünür ama müşteri restoranı kapalı
 * görür. Açmadan önce bu durum kontrol edilir; eksik varsa ne yapılacağı anlatılır.
 */

type Reason = "None" | "NoWorkingHours" | "OutsideWorkingHours" | "TemporarilyClosed";

function formatLocal(utc?: string | null): string {
  if (!utc) return "";
  const d = new Date(utc);
  const opts: Intl.DateTimeFormatOptions = { timeZone: "Europe/Nicosia", hour: "2-digit", minute: "2-digit" };
  const day = (x: Date) => x.toLocaleDateString("tr-TR", { timeZone: "Europe/Nicosia" });
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const time = d.toLocaleTimeString("tr-TR", opts);
  if (day(d) === day(now)) return `bugün ${time}`;
  if (day(d) === day(tomorrow)) return `yarın ${time}`;
  return d.toLocaleString("tr-TR", { ...opts, weekday: "long", day: "numeric", month: "long" });
}

function errorText(err: any, fallback: string): string {
  if (err?.response?.status === 403) return "Bu restoranı açıp kapatma yetkiniz yok.";
  const data = err?.response?.data;
  if (Array.isArray(data) && data[0]?.description) return data[0].description;
  return fallback;
}

interface Props {
  /** Verilmezse bu sekmenin aktif restoranı (SuperAdmin sipariş yönetiminde seçilen restoran verilir). */
  restaurantId?: string;
}

function OpenCloseRestaurantComponent({ restaurantId }: Props = {}) {
  const navigate = useNavigate();
  const rId = restaurantId ?? getActiveRestaurantId();
  const workingHoursPath = restaurantId ? "/adminWorkingHours" : "/workingHours";

  const [detail, setDetail] = useState<IRestaurantDetail>();
  const [availability, setAvailability] = useState<IRestaurantAvailability | null>(null);
  const [busy, setBusy] = useState(false);
  const [popup, setPopup] = useState<{ reason: Reason; availability: IRestaurantAvailability; afterOpen: boolean } | null>(null);
  const [error, setError] = useState("");

  const loadAvailability = useCallback(async (): Promise<IRestaurantAvailability | null> => {
    if (!rId) return null;
    try {
      const res = await getAvailability(rId);
      setAvailability(res.data);
      return res.data;
    } catch {
      setAvailability(null); // durum okunamazsa yalnız bayrağa göre davranılır
      return null;
    }
  }, [rId]);

  const load = useCallback(async () => {
    if (!rId) return;
    getRestaurantById(rId).then((res) => setDetail(res.data)).catch(() => {});
    loadAvailability();
  }, [rId, loadAvailability]);

  useEffect(() => { load(); }, [load]);

  const enabled = !!detail?.restourantIsEnabled;
  const reason = (availability?.closedReason ?? "None") as Reason;
  // Bayrak açık ama müşteri tarafında kapalı: saat yok / saat dışı / geçici kapatma
  const notTakingOrders = enabled && !!availability && !availability.isOpenNow;

  const setFlag = async (value: boolean) => {
    if (!detail?.id) return false;
    try {
      await ChangeRestaurantStatus(value, detail.id);
      return true;
    } catch (err) {
      setError(errorText(err, value ? "Restoran açılamadı." : "Restoran kapatılamadı."));
      return false;
    }
  };

  const toggle = async () => {
    if (!detail?.id || busy) return;
    setBusy(true);
    setError("");
    try {
      if (enabled) {
        if (await setFlag(false)) await load();
        return;
      }
      // Açmadan önce: restoran açılınca gerçekten sipariş alabilecek mi?
      const current = await loadAvailability();
      const r = (current?.closedReason ?? "None") as Reason;
      if (!current || r === "None") {
        if (await setFlag(true)) await load();
        return;
      }
      setPopup({ reason: r, availability: current, afterOpen: false });
    } finally {
      setBusy(false);
    }
  };

  /** Popup'tan: eksik olsa da bayrağı aç (koşul sağlanınca sipariş kendiliğinden başlar). */
  const openAnyway = async () => {
    setBusy(true);
    try {
      if (await setFlag(true)) { await load(); setPopup(null); }
    } finally {
      setBusy(false);
    }
  };

  /** Geçici kapatmayı kaldır, bayrak kapalıysa aç, sonra durumu yeniden değerlendir. */
  const liftClosureAndOpen = async () => {
    if (!rId) return;
    setBusy(true);
    setError("");
    try {
      await reopenRestaurant(rId);
      if (!enabled && !(await setFlag(true))) return;
      await load();
      const after = await loadAvailability();
      const r = (after?.closedReason ?? "None") as Reason;
      // Kapatma kalktı ama başka bir engel varsa (ör. saat dışı) onu anlat
      if (after && r !== "None" && r !== "TemporarilyClosed") setPopup({ reason: r, availability: after, afterOpen: true });
      else setPopup(null);
    } catch (err) {
      setError(errorText(err, "Geçici kapatma kaldırılamadı."));
    } finally {
      setBusy(false);
    }
  };

  const goToWorkingHours = () => { setPopup(null); navigate(workingHoursPath); };

  const label = !enabled ? "Kapalı" : notTakingOrders ? "Sipariş almıyor" : "Açık";
  const tone = !enabled ? "closed" : notTakingOrders ? "warn" : "open";

  return (
    <>
      <div className={`db-resto-toggle db-resto-toggle--${tone}`}>
        <div className="db-resto-dot"></div>
        <div className="db-resto-indicator">
          <span className="db-resto-status-label">Restoran Durumu</span>
          <span className="db-resto-status-value">
            {label}
            {notTakingOrders && (
              <button type="button" className="db-resto-why"
                onClick={() => setPopup({ reason, availability: availability!, afterOpen: true })}>
                Neden?
              </button>
            )}
          </span>
        </div>
        <div className="form-check form-switch mb-0">
          {busy
            ? <span className="spinner-border spinner-border-sm" />
            : <input
                className="db-resto-switch form-check-input"
                type="checkbox"
                aria-label="Restoranı aç / kapat"
                checked={enabled}
                disabled={!detail}
                onChange={toggle}
              />}
        </div>
      </div>

      {error && !popup && (
        <div className="db-resto-error" role="alert">
          <i className="bi bi-exclamation-circle-fill"></i>
          <span>{error}</span>
          <button type="button" onClick={() => setError("")} aria-label="Kapat"><i className="bi bi-x-lg"></i></button>
        </div>
      )}

      {popup && (
        <OpenRequirementsModal
          reason={popup.reason}
          availability={popup.availability}
          alreadyOpen={enabled}
          busy={busy}
          error={error}
          onClose={() => { setPopup(null); setError(""); }}
          onGoToWorkingHours={goToWorkingHours}
          onOpenAnyway={openAnyway}
          onLiftClosure={liftClosureAndOpen}
        />
      )}
    </>
  );
}

function OpenRequirementsModal({
  reason, availability, alreadyOpen, busy, error, onClose, onGoToWorkingHours, onOpenAnyway, onLiftClosure,
}: {
  reason: Reason;
  availability: IRestaurantAvailability;
  alreadyOpen: boolean;
  busy: boolean;
  error: string;
  onClose: () => void;
  onGoToWorkingHours: () => void;
  onOpenAnyway: () => void;
  onLiftClosure: () => void;
}) {
  const reopensAt = formatLocal(availability.reopensAtUtc);
  const note = availability.note || availability.activeOverride?.reason;

  let icon = "bi-clock-history";
  let title = "";
  let intro: JSX.Element | string = "";
  let steps: string[] = [];

  if (reason === "NoWorkingHours") {
    icon = "bi-calendar-x";
    title = "Çalışma saatleri tanımlı değil";
    intro = "Restoranın sipariş alabilmesi için haftalık çalışma saatlerinin girilmiş olması gerekir. Saat tanımlı olmadığında restoran açık konumda olsa bile müşteriler restoranı kapalı görür.";
    steps = [
      "“Çalışma Saatlerini Tanımla” butonuyla Çalışma Saatleri sayfasına gidin.",
      "Restoranın çalıştığı her gün için açılış ve kapanış saatini girin (gece yarısını geçen saatler de girilebilir, ör. 18:00 – 02:00).",
      "Kaydedin ve Dashboard'a dönüp restoranı açın.",
    ];
  } else if (reason === "OutsideWorkingHours") {
    icon = "bi-moon-stars";
    title = "Şu an çalışma saatleri dışındasınız";
    intro = reopensAt
      ? <>Haftalık programınıza göre restoran şu an kapalı. Restoranı açık konumda bırakırsanız <strong>{reopensAt}</strong> itibarıyla siparişler kendiliğinden gelmeye başlar.</>
      : "Haftalık programınıza göre restoran şu an kapalı. Restoranı açık konumda bırakırsanız bir sonraki çalışma saatinde siparişler kendiliğinden gelmeye başlar.";
    steps = [
      "Programa göre çalışmak yeterliyse restoranı açık konumda bırakın; başka bir şey yapmanız gerekmez.",
      "Şimdi sipariş almak istiyorsanız Çalışma Saatleri sayfasında bugünün saatlerini güncelleyin ya da bugün için “özel gün çalışması” ekleyin.",
      "Bugünün saatleri yanlış görünüyorsa gün ve saatleri orada kontrol edin.",
    ];
  } else {
    icon = "bi-pause-circle";
    title = "Restoran geçici olarak kapatılmış";
    intro = (
      <>
        Restoran için bir kapatma kaydı yürürlükte
        {reopensAt ? <>; <strong>{reopensAt}</strong> itibarıyla kendiliğinden kalkacak.</> : <> ve <strong>süresiz</strong> — siz kaldırana kadar sipariş alınmaz.</>}
        {note && <><br /><span className="ocr-note">Not: “{note}”</span></>}
      </>
    );
    steps = [
      "Hemen sipariş almak için “Kapatmayı Kaldır ve Aç” butonuna basın.",
      "Kapatma kalktıktan sonra restoran haftalık çalışma saatlerine göre sipariş almaya başlar.",
      "İleri tarihli kapatmaları (tatil vb.) Çalışma Saatleri sayfasından görebilir ve iptal edebilirsiniz.",
    ];
  }

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dinein-modal dinein-modal--narrow ocr" role="dialog" aria-modal="true" aria-labelledby="ocr-title">
        <div className="dinein-modal__head">
          <div className="ocr__head">
            <span className="ocr__icon"><i className={`bi ${icon}`}></i></span>
            <div>
              <div className="dinein-modal__title" id="ocr-title">{title}</div>
              <div className="dinein-modal__sub">
                {alreadyOpen ? "Restoran açık konumda ama şu an sipariş almıyor." : "Restoran açılınca sipariş alamayacak."}
              </div>
            </div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={busy}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          <p className="ocr__intro">{intro}</p>
          <div className="ocr__steps-title">Yapmanız gerekenler</div>
          <ol className="ocr__steps">
            {steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          {error && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}
        </div>

        <div className="dinein-modal__foot ocr__foot">
          {reason === "TemporarilyClosed" ? (
            <>
              <button className="dinein-btn dinein-btn--ghost" onClick={onGoToWorkingHours} disabled={busy}>
                <i className="bi bi-calendar-week me-1"></i>Çalışma Saatleri
              </button>
              <button className="dinein-btn dinein-btn--primary" onClick={onLiftClosure} disabled={busy}>
                {busy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-play-circle me-1"></i>}
                Kapatmayı Kaldır ve Aç
              </button>
            </>
          ) : reason === "NoWorkingHours" ? (
            <>
              {!alreadyOpen && (
                <button className="dinein-btn dinein-btn--ghost" onClick={onOpenAnyway} disabled={busy}
                  title="Restoran açık konuma gelir; çalışma saati girilene kadar sipariş gelmez">
                  Yine de aç
                </button>
              )}
              <button className="dinein-btn dinein-btn--primary" onClick={onGoToWorkingHours} disabled={busy}>
                <i className="bi bi-calendar-plus me-1"></i>Çalışma Saatlerini Tanımla
              </button>
            </>
          ) : (
            <>
              <button className="dinein-btn dinein-btn--ghost" onClick={onGoToWorkingHours} disabled={busy}>
                <i className="bi bi-calendar-week me-1"></i>Çalışma Saatlerine Git
              </button>
              {alreadyOpen ? (
                <button className="dinein-btn dinein-btn--primary" onClick={onClose} disabled={busy}>Tamam</button>
              ) : (
                <button className="dinein-btn dinein-btn--primary" onClick={onOpenAnyway} disabled={busy}>
                  {busy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
                  Açık Konuma Getir
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OpenCloseRestaurantComponent;
